import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { schoolAPI } from '@/services/api';
import { GraduationCap, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MapFilter from '../components/MapFilter';
import SaveSearchButton from '../components/SaveSearchButton';
import FilterGroup from "@/components/FilterGroup";
import SearchResultCard from "@/components/SearchResultCard";
import PaginationCard from "@/components/PaginationCard";
import { handleUpdateFilter } from "@/utils/filterUtil";
import { injectAds } from "@/utils/adUtil";
import AdSlot from "@/components/AdSlot";

const DISCIPLINES = ['dressage', 'show_jumping', 'vaulting', 'trail', 'western', 'classical', 'therapy'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'competition'];
const AGE_GROUPS = ['kids', 'teens', 'adults', 'all_ages'];
const FACILITIES = ['indoor_arena', 'outdoor_arena', 'cross_country', 'stabling', 'clubhouse'];
const PRICING_UNITS = ['lesson', 'hour', 'month', 'package'];

export default function RidingSchoolListingPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [pages, setPages] = useState(1);
	const [loading, setLoading] = useState(true);
	const [showFilters, setShowFilters] = useState(false);
	const [geoActive, setGeoActive] = useState(false);
	const [pin, setPin] = useState(null);
	const [mapSearchOpen, setMapSearchOpen] = useState(false);
	const [radius, setRadius] = useState(25);

	const [filters, setFilters] = useState({
		discipline: searchParams.get('discipline') || '',
		level: searchParams.get('level') || '',
		age_group: searchParams.get('age_group') || '',
		facility: searchParams.get('facility') || '',
		pricing_unit: searchParams.get('pricing_unit') || '',
		min_price: searchParams.get('min_price') || '',
		max_price: searchParams.get('max_price') || '',
		location: searchParams.get('location') || '',
		lat: searchParams.get('lat') || '',
		lng: searchParams.get('lng') || '',
		radius: searchParams.get('radius') || '',
		verified_only: searchParams.get('verified_only') || '',
		sort: searchParams.get('sort') || 'newest',
		page: parseInt(searchParams.get('page') || '1'),
	});

	useEffect(() => {
		setLoading(true);
		const params = {};
		Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) params[k] = v; });
		schoolAPI.list(params).then((r) => {
			setItems(injectAds(r.data.items || [], filters.page));
			setTotal(r.data.total || 0);
			setPages(r.data.pages || 1);
		}).catch(() => {}).finally(() => setLoading(false));
	}, [filters]);

	const updateFilter = (key, value) => {
		handleUpdateFilter(key, value, filters, setFilters, setSearchParams)
	};

	const clearFilters = () => {
		const cleared = {
			discipline: '',
			level: '',
			age_group: '',
			facility: '',
			pricing_unit: '',
			min_price: '',
			max_price: '',
			location: '',
			verified_only: '',
			sort: 'newest',
			page: 1,
			lat: '',
			lng: '',
			radius: ''
		};
		setPin(null);
		setFilters(cleared);
		setGeoActive(false);
		setSearchParams({});
	};

	const changePage = (p) => updateFilter('page', p);
	const hasActiveFilters = filters.discipline || filters.level ||
		filters.age_group || filters.facility || filters.location || filters.min_price || filters.max_price ||
		filters.verified_only ||  geoActive || pin;

	return (
		<div className="pt-20 md:pt-24 min-h-screen" data-testid="school-listing-page">
			{/* Header */}
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="flex items-center gap-3 mb-2">
						<GraduationCap className="w-7 h-7 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('school.title')}
						</h1>
					</div>
					<p className="text-[#666055]">{total} {t('common.results')}</p>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex gap-8">
					{/* Sidebar Filters - Desktop */}
					<aside className="hidden lg:block w-72 shrink-0">
						<div className="sticky top-24 space-y-6">
							<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
								{t('horse.filters')}
							</h3>
							{hasActiveFilters && (
								<button onClick={clearFilters} className="text-xs text-[#8B4513] hover:underline"
												data-testid="clear-filters">
									{t('horse.clear_filters')}
								</button>
							)}

							<FilterGroup label={ t('school.discipline') }>
								<Select value={filters.discipline || 'all'}
												onValueChange={(v) => updateFilter('discipline', v === 'all' ? '' : v)}>
									<SelectTrigger data-testid="filter-discipline" className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ DISCIPLINES.map(d =>
											<SelectItem key={d} value={d}>{t(`school.discipline_opt.${d}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('school.level') }>
								<Select value={filters.level || 'all'} onValueChange={(v) => updateFilter('level', v === 'all' ? '' : v)}>
									<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ LEVELS.map(l =>
											<SelectItem key={l} value={l}>{t(`school.level_opt.${l}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('school.age_group') }>
								<Select value={filters.age_group || 'all'}
												onValueChange={(v) => updateFilter('age_group', v === 'all' ? '' : v)}>
									<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ AGE_GROUPS.map(a =>
											<SelectItem key={a} value={a}>{t(`school.age_opt.${a}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('school.facility') }>
								<Select value={filters.facility || 'all'} onValueChange={(v) => updateFilter('facility', v === 'all' ? '' : v)}>
									<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ FACILITIES.map(f =>
											<SelectItem key={f} value={f}>{t(`school.facility_opt.${f}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('transport.pricing_unit') }>
								<Select value={filters.pricing_unit || 'all'}
												onValueChange={(v) => updateFilter('pricing_unit', v === 'all' ? '' : v)}>
									<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ PRICING_UNITS.map(u =>
											<SelectItem key={u} value={u}>{t(`school.unit.${u}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('horse.price') }>
								<div className="flex gap-2">
									<Input type="number" placeholder="Min" value={filters.min_price}
												 onChange={e => updateFilter('min_price', e.target.value)}
												 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
									<Input type="number" placeholder="Max" value={filters.max_price}
												 onChange={e => updateFilter('max_price', e.target.value)}
												 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
								</div>
							</FilterGroup>

							<FilterGroup label={ t('horse.location') }>
								<Input placeholder={t('horse.location')} value={filters.location}
											 onChange={e => updateFilter('location', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							</FilterGroup>
							<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
								<input type="checkbox" checked={filters.verified_only === 'true'}
											 onChange={e => updateFilter('verified_only', e.target.checked ? 'true' : '')}
											 data-testid="filter-mobile" />
								{t('common.only_verified_sellers')}
							</label>
						</div>
					</aside>

					{/* Main Content */}
					<div className="flex-1 min-w-0">
						{/* Sort & Mobile Filter */}
						<div className="flex items-center justify-end mb-6 gap-4">
							<button
								onClick={() => setShowFilters(true)}
								className="lg:hidden flex items-center gap-2 px-4 py-2 border border-[#E6E2D6] rounded-sm text-sm
										text-[#1A3C34] hover:bg-[#F2F0E9]"
								data-testid="mobile-filter-toggle"
							>
								<SlidersHorizontal className="w-4 h-4" /> {t('horse.filters')}
							</button>
							<SaveSearchButton adType="school" />
							<Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
								<SelectTrigger className="w-48 bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="newest">{t('horse.sort_newest')}</SelectItem>
									<SelectItem value="price_asc">{t('horse.sort_price_asc')}</SelectItem>
									<SelectItem value="price_desc">{t('horse.sort_price_desc')}</SelectItem>
									{geoActive && <SelectItem value="distance">{t('search.distance')}</SelectItem>}
								</SelectContent>
							</Select>
						</div>

						{/* Map Filter + Save Search */}
						<div className="flex items-start justify-between gap-3 mb-2">
							<div className="flex-1">
								<MapFilter
									pin={ pin }
									setPin={ setPin }
									radius={ radius}
									setRadius={ setRadius }
									open={ mapSearchOpen }
									setOpen={ setMapSearchOpen }
									geoActive={ geoActive }
									setGeoActive={ setGeoActive }
									onSearch={() => {
										const newFilters = { ...filters, lat: String(pin.lat), lng: String(pin.lng), radius: String(radius), sort: 'distance', page: 1 };
										setFilters(newFilters);
										const sp = new URLSearchParams();
										Object.entries(newFilters).forEach(([k, v]) => { if (v && v !== '' && v !== 1) sp.set(k, v); });
										setSearchParams(sp);
									}}
								/>
							</div>
						</div>

						<div className="flex md:hidden items-start justify-between mb-2">
							<SaveSearchButton adType="school" isMobile={ true }/>
						</div>

						{/* Grid */}
						{loading ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
								{[...Array(6)].map((_, i) => (
									<div key={i} className="bg-white rounded-sm border border-[#E6E2D6] overflow-hidden">
										<div className="aspect-[4/3] img-skeleton" />
										<div className="p-5 space-y-3">
											<div className="h-5 w-3/4 img-skeleton rounded" />
											<div className="h-7 w-1/3 img-skeleton rounded" />
											<div className="h-4 w-1/2 img-skeleton rounded" />
										</div>
									</div>
								))}
							</div>
						) : items.length === 0 ? (
							<div className="text-center py-20" data-testid="no-schools">
								<GraduationCap className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
								<p className="text-lg text-[#666055]">{t('school.no_results')}</p>
								{hasActiveFilters && (
									<Button onClick={clearFilters}
													className="mt-4 bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm">
										{t('horse.clear_filters')}
									</Button>
								)}
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="school-grid">
								{items.map((it, i) =>
									it.type === 'listing' ? (
										<SearchResultCard key={i} ad={ it } adType='school' uri="schools" />
									) : (
										<AdSlot
											key={it.id}
											id={it.id}
											provider="gam"
											placement="search_results_infeed"
										/>
									)
								)}
							</div>
						)}

						{ pages > 1 && (
							<PaginationCard pages={pages} page={ filters.page } changePage={ changePage } />
						)}
					</div>
				</div>
			</div>

			{/* Mobile Filters Drawer */}
			{showFilters && (
				<div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-drawer">
					<div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
					<div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6 overflow-auto animate-slide-in">
						<div className="flex items-center justify-between mb-6">
							<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
								{t('horse.filters')}
							</h3>
							<button onClick={() => setShowFilters(false)}>
								<X className="w-5 h-5" />
							</button>
						</div>
						<div className="space-y-4">
							<Select value={filters.discipline} onValueChange={(v) => updateFilter('discipline', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('event.all_disciplines')} /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('event.all_disciplines')}</SelectItem>
									{ DISCIPLINES.map(d =>
										<SelectItem key={d} value={d}>{t(`school.discipline_opt.${d}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<Select value={filters.gender} onValueChange={(v) => updateFilter('level', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('event.all_levels')} /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('event.all_levels')}</SelectItem>
									{ LEVELS.map(l =>
										<SelectItem key={l} value={l}>{t(`school.level_opt.${l}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<Select value={filters.age_group} onValueChange={(v) => updateFilter('age_group', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('school.all_age_groups')} /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('school.all_age_groups')}</SelectItem>
									{ AGE_GROUPS.map(a =>
										<SelectItem key={a} value={a}>{t(`school.age_opt.${a}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<Select value={filters.facility} onValueChange={(v) => updateFilter('facility', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('school.all_facilities')} /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('school.all_facilities')}</SelectItem>
									{ FACILITIES.map(f =>
										<SelectItem key={f} value={f}>{t(`school.facility_opt.${f}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<Select value={filters.pricing_unit} onValueChange={(v) => updateFilter('pricing_unit', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('transport.all_pricing_units')} /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('transport.all_pricing_units')}</SelectItem>
									{ PRICING_UNITS.map(u =>
										<SelectItem key={u} value={u}>{t(`school.unit.${u}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<div className="flex gap-2">
								<Input data-testid="filter-min-price" type="number" placeholder={t('horse.min_price')}
											 value={filters.min_price} onChange={e => updateFilter('min_price', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
								<Input data-testid="filter-max-price" type="number" placeholder={t('horse.max_price')}
											 value={filters.max_price} onChange={e => updateFilter('max_price', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							</div>
							<Input placeholder={t('horse.location')} value={filters.location}
										 onChange={e => updateFilter('location', e.target.value)}
										 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
								<input type="checkbox" checked={filters.verified_only === 'true'}
											 onChange={e => updateFilter('verified_only', e.target.checked ? 'true' : '')}
											 data-testid="filter-mobile" />
								{t('common.only_verified_sellers')}
							</label>
							<Button onClick={() => setShowFilters(false)}
											className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10">
								{t('search.apply')}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}